import { BallMovement, FrameStencil, MarkerType, Movement, MovementType, StencilType } from "../../types";
import {
  getBallActions,
  getHandoffActions,
  getPlayerAndPossessionActions,
  getPlayerMarkers,
  isPlayer,
} from "./frame-stencil-filters";

export interface CreateMovementChainBase {
  stencils: FrameStencil[];
}

export interface CreateBallMovementChain extends CreateMovementChainBase {
  movement: BallMovement;
}

export interface CreatePlayerMovementChain extends CreateMovementChainBase {
  movement: Movement;
}

export const createPlayerMovementChain = ({ movement, stencils }: CreatePlayerMovementChain): Movement[] => {
  const movementAction = getPlayerAndPossessionActions(stencils).find((s) => s.id === movement.id);
  if (!movementAction || !movementAction.nextPlayerAction) return [movement];
  else return [movement, ...createPlayerMovementChain({ movement: { id: movementAction.nextPlayerAction }, stencils })];
};

export const createBallMovementChain = ({ movement, stencils }: CreateBallMovementChain): BallMovement[] => {
  const passes = [...getBallActions(stencils), ...getHandoffActions(stencils)];
  const movementStencil = [...getPlayerMarkers(stencils), ...passes].find((s) => s.id === movement.id);
  if (!movementStencil) return [movement];
  else if (isPlayer(movementStencil)) {
    const movementStartTime = movement.movementType === MovementType.POSSESSION ? movement.startTime : 0;
    const nextPass = passes.find((p) => p.prevPlayer === movementStencil.id && p.startTime >= movementStartTime);

    if (!nextPass) return [movement];
    else
      return [
        movement,
        ...createBallMovementChain({ movement: { id: nextPass.id, movementType: MovementType.BALL_ACTION }, stencils }),
      ];
  } else
    return [
      movement,
      ...(movementStencil.nextPlayer
        ? createBallMovementChain({
            movement: {
              id: movementStencil.nextPlayer,
              movementType: MovementType.POSSESSION,
              startTime: movementStencil.endTime,
            },
            stencils: stencils,
          })
        : []),
    ];
};

export const refreshMovements = (stencils: FrameStencil[]): FrameStencil[] => {
  return stencils.map((stencil): FrameStencil => {
    switch (stencil.stencilType) {
      case StencilType.ACTION:
        return stencil;

      case StencilType.MARKER: {
        switch (stencil.markerType) {
          case MarkerType.PLAYER: {
            if (!stencil.movements[0]) return stencil;
            else
              return { ...stencil, movements: createPlayerMovementChain({ movement: stencil.movements[0], stencils }) };
          }

          case MarkerType.BALL: {
            if (!stencil.movements[0]) return stencil;
            else
              return { ...stencil, movements: createBallMovementChain({ movement: stencil.movements[0], stencils }) };
          }

          default:
            return stencil;
        }
      }
    }
  });
};
